import React, { Component } from "react";
import clsx from "clsx";
import moment from "moment";

import TaskRow from "./TaskRow";
import { I, groupIcon } from "./common/icons";
import { SERVICES, SERVICE_ORDER } from "./backofficeConstants";
import { fmt, isOverdue, isComplete, periodLabel, today } from "./backofficeUtils";

/**
 * A backoffice client accordion: header with pills and progress, then the service panel
 * (service picker, period navigation, task rows grouped by group name).
 */
class ClientCard extends Component {
	renderHeader() {
		const { client, tasks, open, onToggle } = this.props;
		const all = tasks;
		const doneAll = all.filter(isComplete).length;
		const overdue = all.filter(t => isOverdue(t)).length;
		const pending = all.filter(t => t.status === "pending_client").length;
		const next = all
			.filter(t => !isComplete(t) && t.dueDate >= today())
			.sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))[0];
		const active = SERVICE_ORDER.filter(s => all.some(t => t.service === s));
		const pct = all.length ? Math.round((doneAll / all.length) * 100) : 0;

		return (
			<div className="bo-client__head ft-bo-client-head" onClick={() => onToggle(client.id)}>
				<I.chev className={clsx("bo-chev", { "bo-chev--open": open })} />
				<div>
					<div className="bo-client__name">
						<h3>{client.name}</h3>
						<span className="bo-pill bo-pill--bo">Backoffice</span>
						{(client.industry || client.yearEnd) && (
							<span className="bo-pill bo-pill--grey">
								{[client.industry, client.yearEnd ? `YE ${moment(client.yearEnd).isValid() ? moment(client.yearEnd).format("MMM D") : client.yearEnd}` : null].filter(Boolean).join(" · ")}
							</span>
						)}
						{overdue > 0 && <span className="bo-pill bo-pill--red">{overdue} overdue</span>}
						{pending > 0 && <span className="bo-pill bo-pill--amber">{pending} pending client</span>}
					</div>
					<div className="bo-client__meta">
						<span>
							<I.user />
							{client.contact ? [client.contact.name, client.contact.relation, client.contact.email].filter(Boolean).join(" · ") : "No client contact — add one to enable email automations"}
						</span>
						<span>
							<I.briefcase />
							{active.map(s => SERVICES[s].label).join(" · ") || "No services yet"}
						</span>
						{next && (
							<span>
								<I.clock />
								Next due {fmt(next.dueDate)} · {next.title}
							</span>
						)}
					</div>
				</div>
				<div className="bo-client__right">
					<div className="bo-svc-dots" title="Services">
						{active.map(s => (
							<span key={s} className="bo-svc-dot" style={{ background: SERVICES[s].color }} />
						))}
					</div>
					<div className="bo-progress">
						<span>
							<b>{doneAll}</b> / {all.length} Complete
						</span>
						<span className="bo-bar">
							<i className={clsx({ "bo-bar--green": doneAll === all.length && all.length })} style={{ width: `${pct}%` }} />
						</span>
					</div>
				</div>
			</div>
		);
	}

	renderTaskList(periodTasks) {
		const out = [];
		let i = 0;
		while (i < periodTasks.length) {
			const g = periodTasks[i].groupName;
			if (g) {
				let j = i;
				const members = [];
				while (j < periodTasks.length && periodTasks[j].groupName === g) {
					members.push(periodTasks[j]);
					j++;
				}
				const emailCount = members.filter(t => t.emailAutomation).length;
				out.push(
					<div key={`g-${g}-${members[0].id}`} className="bo-tgroup">
						<div className="bo-tgroup__head">
							{groupIcon(members[0].groupIcon)}
							{g}
							<span className="bo-tgroup__right">
								{emailCount ? <span className="bo-tgroup__connected">{emailCount} Client Action{emailCount > 1 ? "s" : ""} Connected</span> : `${members.length} Sub-tasks`}
							</span>
						</div>
						<div className="bo-tasks">{members.map(t => this.renderRow(t))}</div>
					</div>,
				);
				i = j;
			} else {
				out.push(this.renderRow(periodTasks[i]));
				i++;
			}
		}
		return out;
	}

	renderRow(task) {
		const { users, flashTaskId, openAccounts, handlers } = this.props;
		return (
			<TaskRow
				key={task.id}
				task={task}
				users={users}
				flash={flashTaskId === task.id}
				accountsOpen={openAccounts.has(task.id)}
				onToggleComplete={handlers.onToggleComplete}
				onStatusChange={handlers.onStatusChange}
				onEdit={handlers.onEdit}
				onDelete={handlers.onDelete}
				onOpenEmail={handlers.onOpenEmail}
				onToggleAccounts={handlers.onToggleAccounts}
				onToggleAccount={handlers.onToggleAccount}
				onAddAccount={handlers.onAddAccount}
				onRemoveAccount={handlers.onRemoveAccount}
			/>
		);
	}

	renderPanel() {
		const { client, tasks, service, period, passFilters, handlers } = this.props;
		const S = SERVICES[service];
		const svcTasks = tasks.filter(t => t.service === service);
		const inPeriod = svcTasks.filter(t => t.periodStart === period.start);
		const visible = inPeriod.filter(passFilters);
		const total = inPeriod.length;
		const done = inPeriod.filter(isComplete).length;
		const pct = total ? Math.round((done / total) * 100) : 0;
		const otherPeriods = svcTasks.length - total;

		return (
			<>
				<div className="bo-svc-bar">
					<div className="bo-svc-select">
						<label htmlFor={`svc-${client.id}`}>Service</label>
						<select id={`svc-${client.id}`} className="ft-bo-service-select" value={service} onChange={e => handlers.onServiceChange(client.id, e.target.value)}>
							{SERVICE_ORDER.map(s => {
								const n = tasks.filter(t => t.service === s).length;
								return (
									<option key={s} value={s}>
										{SERVICES[s].label}
										{n ? ` (${n})` : ""}
									</option>
								);
							})}
						</select>
					</div>
					<div className="bo-period">
						<button className="bo-period__nav" type="button" aria-label="Previous period" onClick={() => handlers.onPeriodShift(client.id, service, -1)}>
							<I.chevL />
						</button>
						<I.cal />
						<span>{periodLabel(service, period)}</span>
						<button className="bo-period__nav" type="button" aria-label="Next period" onClick={() => handlers.onPeriodShift(client.id, service, 1)}>
							<I.chevR />
						</button>
					</div>
					<span className="bo-spacer" />
					<button className="bo-btn bo-btn--sm" type="button" onClick={() => handlers.onOpenCalendar(client)}>
						<I.cal />
						Open in calendar
					</button>
					<button className="bo-btn bo-btn--sm ft-bo-prepopulate" type="button" onClick={() => handlers.onOpenTemplate(client, service, period)}>
						<I.repeat />
						Prepopulate from template
					</button>
					<button className="bo-btn bo-btn--sm bo-btn--primary ft-bo-add-task" type="button" onClick={() => handlers.onAddTask(client, service, period)}>
						<I.plus />
						Add task
					</button>
				</div>
				<div className="bo-svc-summary">
					<span className="bo-svc-summary__pname">
						<span className="bo-svc-swatch" style={{ background: S.color }} />
						{S.label}
					</span>
					<span className="bo-svc-summary__cnt">{total} Tasks</span>
					<span className="bo-legend-st">
						<i className="g" />
						Complete
						<i className="b" />
						In progress
						<i className="a" />
						Pending client
						<i className="r" />
						Overdue
						<i className="n" />
						Not started
					</span>
					<span className="bo-spacer" />
					<div className="bo-progress">
						<span>
							<b>{done}</b> / {total} Complete
						</span>
						<span className="bo-bar">
							<i className={clsx({ "bo-bar--green": pct === 100 && total })} style={{ width: `${pct}%` }} />
						</span>
					</div>
				</div>

				{!total && (
					<div className="bo-empty">
						<b>
							No {S.label.toLowerCase()} tasks for {periodLabel(service, period).toLowerCase()}
						</b>
						{otherPeriods ? `${otherPeriods} ${S.label.toLowerCase()} task${otherPeriods > 1 ? "s" : ""} exist in other periods. ` : ""}
						Start from a template or add a one-off task.
						<div className="bo-empty__row">
							<button className="bo-btn bo-btn--primary bo-btn--sm" type="button" onClick={() => handlers.onOpenTemplate(client, service, period)}>
								<I.repeat />
								Prepopulate from template
							</button>
							<button className="bo-btn bo-btn--sm" type="button" onClick={() => handlers.onAddTask(client, service, period)}>
								<I.plus />
								Add task
							</button>
						</div>
					</div>
				)}
				{total > 0 && !visible.length && (
					<div className="bo-empty">
						<b>No tasks match the current filters</b>Clear the assignee or status filter to see all {total} tasks.
					</div>
				)}
				{visible.length > 0 && (
					<>
						<div className="bo-tasks">{this.renderTaskList(visible)}</div>
						<button className={clsx("bo-addtask", S.add && `bo-addtask--${S.add}`)} type="button" onClick={() => handlers.onAddTask(client, service, period)}>
							<I.plus />+ Add Task to {S.label}
						</button>
					</>
				)}
			</>
		);
	}

	render() {
		const { open, client } = this.props;
		return (
			<div className={clsx("bo-client", { "bo-client--open": open })} data-client={client.id}>
				{this.renderHeader()}
				<div className="bo-client__body">
					<div className="bo-client__inner">{open && this.renderPanel()}</div>
				</div>
			</div>
		);
	}
}

export default ClientCard;
